import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Download,
  Shield,
  Database,
  FileJson,
  FileSpreadsheet,
  Clock,
  AlertTriangle,
  CheckCircle,
  HardDrive,
  Users,
  Package,
  ShoppingCart,
  Building2,
  Loader2,
  Trash2,
  RefreshCw,
  Info,
  Calendar,
  DollarSign,
  Upload,
  RotateCcw,
  FileUp,
  CheckSquare,
  Settings,
  Play,
  Zap,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const BackupPage = () => {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [fullStatus, setFullStatus] = useState(null);
  
  // Restore state
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [restoreMode, setRestoreMode] = useState("merge");
  const [restoreResult, setRestoreResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Auto-backup state
  const [autoBackupSettings, setAutoBackupSettings] = useState({
    auto_backup_enabled: true,
    backup_frequency: "weekly",
    backup_day: 0,
    keep_backups_count: 4,
    reminder_enabled: true,
  });
  const [autoBackupStatus, setAutoBackupStatus] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [triggeringBackup, setTriggeringBackup] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/backup/quick-stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/backup/history?limit=10`);
      setHistory(response.data.backups || []);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  }, []);

  const fetchFullStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/backup/status`);
      setFullStatus(response.data);
      // Also update auto-backup settings if present
      if (response.data.settings) {
        setAutoBackupSettings(prev => ({
          ...prev,
          ...response.data.settings,
        }));
      }
    } catch (error) {
      console.error("Error fetching full status:", error);
    }
  }, []);

  const fetchAutoBackupStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/backup/auto/status`);
      setAutoBackupStatus(response.data);
    } catch (error) {
      console.error("Error fetching auto-backup status:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchHistory(), fetchAutoBackupStatus()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchHistory, fetchAutoBackupStatus]);

  const handleDownload = async (type) => {
    setDownloading(type);
    try {
      let endpoint = "";
      let filename = "";
      
      switch (type) {
        case "full":
          endpoint = "/api/backup/download/full";
          filename = `pos_full_backup_${new Date().toISOString().slice(0, 10)}.zip`;
          break;
        case "critical":
          endpoint = "/api/backup/download/critical";
          filename = `pos_critical_backup_${new Date().toISOString().slice(0, 10)}.zip`;
          break;
        case "financial":
          endpoint = "/api/backup/download/financial";
          filename = `pos_financial_backup_${new Date().toISOString().slice(0, 10)}.zip`;
          break;
        default:
          return;
      }
      
      const response = await axios.get(`${API}${endpoint}`, {
        responseType: "blob"
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} backup downloaded successfully!`);
      
      // Refresh stats and history
      fetchStats();
      fetchHistory();
    } catch (error) {
      console.error("Error downloading backup:", error);
      toast.error("Failed to download backup. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  const handleDeleteHistory = async (backupId) => {
    try {
      await axios.delete(`${API}/api/backup/history/${backupId}`);
      toast.success("Backup record deleted");
      fetchHistory();
    } catch (error) {
      toast.error("Failed to delete backup record");
    }
  };

  const handleSaveAutoBackupSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${API}/api/backup/settings`, autoBackupSettings);
      toast.success("Auto-backup settings saved");
      fetchAutoBackupStatus();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleTriggerAutoBackup = async () => {
    setTriggeringBackup(true);
    try {
      const response = await axios.post(`${API}/api/backup/auto/run`);
      if (response.data.backup_id) {
        toast.success("Auto-backup created successfully!");
      } else {
        toast.info(response.data.message || "No backup needed at this time");
      }
      fetchStats();
      fetchHistory();
      fetchAutoBackupStatus();
    } catch (error) {
      console.error("Error triggering backup:", error);
      toast.error("Failed to trigger backup");
    } finally {
      setTriggeringBackup(false);
    }
  };

  // Restore functions
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.zip')) {
      toast.error("Please select a ZIP backup file");
      return;
    }
    
    setRestoreFile(file);
    setRestoreLoading(true);
    setRestorePreview(null);
    setSelectedCollections([]);
    setRestoreResult(null);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API}/api/backup/restore/preview`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setRestorePreview(response.data);
      
      // Select all restorable collections by default
      const restorable = Object.entries(response.data.collections || {})
        .filter(([_, info]) => info.can_restore && info.backup_count > 0)
        .map(([name]) => name);
      setSelectedCollections(restorable);
      
    } catch (error) {
      console.error("Error previewing backup:", error);
      toast.error(error.response?.data?.detail || "Failed to analyze backup file");
      setRestoreFile(null);
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile || selectedCollections.length === 0) {
      toast.error("Please select collections to restore");
      return;
    }
    
    const confirmMessage = restoreMode === "replace" 
      ? "⚠️ WARNING: This will DELETE all current data in the selected collections and replace it with backup data. This cannot be undone!\n\nAre you sure you want to proceed?"
      : "This will add new data from the backup. Existing data will not be changed.\n\nProceed with restore?";
    
    if (!window.confirm(confirmMessage)) return;
    
    setRestoreLoading(true);
    
    try {
      const formData = new FormData();
      formData.append("file", restoreFile);
      formData.append("collections", JSON.stringify(selectedCollections));
      formData.append("mode", restoreMode);
      
      const response = await axios.post(`${API}/api/backup/restore/execute`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setRestoreResult(response.data);
      toast.success(`Restore complete! ${response.data.total_restored} records restored.`);
      
      // Refresh data
      fetchStats();
      fetchHistory();
      
    } catch (error) {
      console.error("Error restoring:", error);
      toast.error(error.response?.data?.detail || "Failed to restore backup");
    } finally {
      setRestoreLoading(false);
    }
  };

  const toggleCollection = (name) => {
    setSelectedCollections(prev => 
      prev.includes(name) 
        ? prev.filter(c => c !== name)
        : [...prev, name]
    );
  };

  const selectAllCollections = () => {
    if (!restorePreview) return;
    const all = Object.entries(restorePreview.collections || {})
      .filter(([_, info]) => info.can_restore && info.backup_count > 0)
      .map(([name]) => name);
    setSelectedCollections(all);
  };

  const deselectAllCollections = () => {
    setSelectedCollections([]);
  };

  const closeRestoreDialog = () => {
    setRestoreOpen(false);
    setRestoreFile(null);
    setRestorePreview(null);
    setSelectedCollections([]);
    setRestoreResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const getBackupUrgency = () => {
    if (!stats) return null;
    
    if (stats.days_since_backup === null) {
      return { level: "critical", message: "No backup has ever been created!", color: "bg-red-100 border-red-500 text-red-700" };
    }
    if (stats.days_since_backup > 30) {
      return { level: "critical", message: `Last backup was ${stats.days_since_backup} days ago - URGENT!`, color: "bg-red-100 border-red-500 text-red-700" };
    }
    if (stats.days_since_backup > 7) {
      return { level: "warning", message: `Last backup was ${stats.days_since_backup} days ago - backup recommended`, color: "bg-yellow-100 border-yellow-500 text-yellow-700" };
    }
    return { level: "ok", message: `Last backup: ${stats.days_since_backup} day(s) ago`, color: "bg-green-100 border-green-500 text-green-700" };
  };

  const urgency = getBackupUrgency();

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-600" />
          <p className="mt-2 text-gray-500">Loading backup status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="backup-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-green-600" />
            Data Backup & Protection
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Protect your business data with regular backups
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setRestoreOpen(true)}
            className="text-blue-600 border-blue-300 hover:bg-blue-50"
            data-testid="restore-btn"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Restore Data
          </Button>
          <Button 
            variant="outline" 
            onClick={() => {
              fetchStats();
              fetchHistory();
            }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Backup Alert */}
      {urgency && (
        <Alert className={urgency.color}>
          {urgency.level === "critical" ? (
            <AlertTriangle className="h-4 w-4" />
          ) : urgency.level === "warning" ? (
            <Clock className="h-4 w-4" />
          ) : (
            <CheckCircle className="h-4 w-4" />
          )}
          <AlertTitle>
            {urgency.level === "critical" ? "Backup Required!" : 
             urgency.level === "warning" ? "Backup Recommended" : "Backup Status"}
          </AlertTitle>
          <AlertDescription>{urgency.message}</AlertDescription>
        </Alert>
      )}

      {/* Quick Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.products?.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Products</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.customers?.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Customers</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ShoppingCart className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.sales?.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Sales</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Building2 className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.suppliers?.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Suppliers</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Backup Options */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Full Backup */}
        <Card className="border-2 border-green-200 bg-green-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <Database className="h-5 w-5" />
              Full Backup
            </CardTitle>
            <CardDescription>
              Complete backup of ALL your data - recommended for regular backups
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>All products & inventory</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>All customers & suppliers</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>All sales & transactions</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>Employees & payslips</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>Settings & configurations</span>
              </div>
            </div>
            <div className="flex gap-2 text-xs text-gray-500">
              <FileJson className="h-4 w-4" />
              <span>JSON + CSV formats</span>
            </div>
            <Button 
              className="w-full bg-green-600 hover:bg-green-700"
              onClick={() => handleDownload("full")}
              disabled={downloading !== null}
              data-testid="full-backup-btn"
            >
              {downloading === "full" ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download Full Backup
            </Button>
          </CardContent>
        </Card>

        {/* Critical Data Backup */}
        <Card className="border-2 border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <HardDrive className="h-5 w-5" />
              Critical Data
            </CardTitle>
            <CardDescription>
              Quick backup of essential business data - faster download
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-blue-600" />
                <span>Products & stock levels</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-blue-600" />
                <span>Customer accounts & balances</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-blue-600" />
                <span>Supplier information</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-blue-600" />
                <span>Recent sales (90 days)</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-blue-600" />
                <span>Employee records</span>
              </div>
            </div>
            <div className="flex gap-2 text-xs text-gray-500">
              <FileSpreadsheet className="h-4 w-4" />
              <span>JSON + CSV formats</span>
            </div>
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={() => handleDownload("critical")}
              disabled={downloading !== null}
              data-testid="critical-backup-btn"
            >
              {downloading === "critical" ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download Critical Data
            </Button>
          </CardContent>
        </Card>

        {/* Financial Backup */}
        <Card className="border-2 border-purple-200 bg-purple-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-700">
              <DollarSign className="h-5 w-5" />
              Financial Data
            </CardTitle>
            <CardDescription>
              All financial records - perfect for your accountant
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-purple-600" />
                <span>All sales transactions</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-purple-600" />
                <span>Customer payments & balances</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-purple-600" />
                <span>Expenses & petty cash</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-purple-600" />
                <span>Cash-up sessions</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-purple-600" />
                <span>Purchase orders & GRVs</span>
              </div>
            </div>
            <div className="flex gap-2 text-xs text-gray-500">
              <FileSpreadsheet className="h-4 w-4" />
              <span>Excel-ready CSV files</span>
            </div>
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700"
              onClick={() => handleDownload("financial")}
              disabled={downloading !== null}
              data-testid="financial-backup-btn"
            >
              {downloading === "financial" ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download Financial Data
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Safety Tips */}
      <Card className="bg-amber-50 border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-700">
            <Info className="h-5 w-5" />
            Backup Best Practices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <h4 className="font-semibold text-amber-800">How Often to Backup:</h4>
              <ul className="list-disc list-inside text-amber-700 space-y-1">
                <li><strong>Daily:</strong> If you process many sales</li>
                <li><strong>Weekly:</strong> Minimum recommended</li>
                <li><strong>Monthly:</strong> Full backup + financial export</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-amber-800">Where to Store Backups:</h4>
              <ul className="list-disc list-inside text-amber-700 space-y-1">
                <li><strong>USB Drive:</strong> Keep one at home, one at shop</li>
                <li><strong>Google Drive / Dropbox:</strong> Upload monthly</li>
                <li><strong>Email to yourself:</strong> For important backups</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Auto-Backup Settings */}
      <Card className="border-2 border-cyan-200 bg-cyan-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-cyan-700">
            <Zap className="h-5 w-5" />
            Automatic Backups
          </CardTitle>
          <CardDescription>
            Configure automatic backups to run on a schedule
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
            <div className="space-y-0.5">
              <Label className="text-base font-medium">Enable Auto-Backups</Label>
              <p className="text-sm text-gray-500">
                Automatically create backups on a schedule
              </p>
            </div>
            <Switch
              checked={autoBackupSettings.auto_backup_enabled}
              onCheckedChange={(checked) => setAutoBackupSettings(prev => ({
                ...prev,
                auto_backup_enabled: checked
              }))}
              data-testid="auto-backup-toggle"
            />
          </div>

          {autoBackupSettings.auto_backup_enabled && (
            <div className="grid md:grid-cols-3 gap-4">
              {/* Frequency */}
              <div className="space-y-2">
                <Label>Backup Frequency</Label>
                <Select
                  value={autoBackupSettings.backup_frequency}
                  onValueChange={(value) => setAutoBackupSettings(prev => ({
                    ...prev,
                    backup_frequency: value
                  }))}
                >
                  <SelectTrigger data-testid="backup-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Keep Count */}
              <div className="space-y-2">
                <Label>Keep Last N Backups</Label>
                <Select
                  value={autoBackupSettings.keep_backups_count?.toString()}
                  onValueChange={(value) => setAutoBackupSettings(prev => ({
                    ...prev,
                    keep_backups_count: parseInt(value)
                  }))}
                >
                  <SelectTrigger data-testid="keep-count-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2 backups</SelectItem>
                    <SelectItem value="4">4 backups</SelectItem>
                    <SelectItem value="7">7 backups</SelectItem>
                    <SelectItem value="14">14 backups</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Reminder Toggle */}
              <div className="space-y-2">
                <Label>Backup Reminders</Label>
                <div className="flex items-center gap-2 h-10">
                  <Switch
                    checked={autoBackupSettings.reminder_enabled}
                    onCheckedChange={(checked) => setAutoBackupSettings(prev => ({
                      ...prev,
                      reminder_enabled: checked
                    }))}
                  />
                  <span className="text-sm text-gray-600">
                    {autoBackupSettings.reminder_enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Status Info */}
          {autoBackupStatus && (
            <div className="p-4 bg-white rounded-lg border">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Last Backup</p>
                  <p className="font-medium">
                    {autoBackupStatus.last_backup 
                      ? formatDate(autoBackupStatus.last_backup.created_at)
                      : "Never"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Days Since Backup</p>
                  <p className={`font-medium ${
                    autoBackupStatus.days_since_backup > 7 
                      ? "text-red-600" 
                      : autoBackupStatus.days_since_backup > 3 
                        ? "text-yellow-600" 
                        : "text-green-600"
                  }`}>
                    {autoBackupStatus.days_since_backup ?? "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <Badge variant="outline" className={
                    autoBackupStatus.backup_overdue 
                      ? "bg-red-50 text-red-700 border-red-200"
                      : "bg-green-50 text-green-700 border-green-200"
                  }>
                    {autoBackupStatus.backup_overdue ? "Overdue" : "Up to date"}
                  </Badge>
                </div>
                <div>
                  <p className="text-gray-500">Frequency</p>
                  <p className="font-medium capitalize">
                    {autoBackupStatus.backup_frequency || "Weekly"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleSaveAutoBackupSettings}
              disabled={savingSettings}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="save-settings-btn"
            >
              {savingSettings ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Settings className="h-4 w-4 mr-2" />
              )}
              Save Settings
            </Button>
            
            <Button
              variant="outline"
              onClick={handleTriggerAutoBackup}
              disabled={triggeringBackup}
              data-testid="trigger-backup-btn"
            >
              {triggeringBackup ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Backup Now
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Backup History */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Backup History
            </CardTitle>
            <CardDescription>Your recent backup downloads</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              fetchFullStatus();
              setDetailsOpen(true);
            }}
          >
            <Info className="h-4 w-4 mr-2" />
            View Details
          </Button>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Database className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No backups yet</p>
              <p className="text-sm">Create your first backup using the buttons above</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Filename</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((backup) => (
                  <TableRow key={backup.id}>
                    <TableCell className="text-sm">
                      {formatDate(backup.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={
                        backup.type === "full" ? "default" :
                        backup.type === "critical" ? "secondary" : "outline"
                      }>
                        {backup.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {backup.filename}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {backup.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteHistory(backup.id)}
                      >
                        <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Database Details</DialogTitle>
            <DialogDescription>
              Complete breakdown of your data by collection
            </DialogDescription>
          </DialogHeader>
          
          {fullStatus && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">
                      {fullStatus.total_documents?.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">Total Records</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">
                      {Object.keys(fullStatus.collections || {}).length}
                    </p>
                    <p className="text-sm text-gray-500">Collections</p>
                  </CardContent>
                </Card>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Collection</TableHead>
                    <TableHead className="text-right">Documents</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(fullStatus.collections || {})
                    .sort((a, b) => (b[1]?.document_count || 0) - (a[1]?.document_count || 0))
                    .map(([name, info]) => (
                      <TableRow key={name}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{info?.display_name || name}</p>
                            <p className="text-xs text-gray-400">{name}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {(info?.document_count || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={restoreOpen} onOpenChange={(open) => !open && closeRestoreDialog()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-blue-600" />
              Restore Data from Backup
            </DialogTitle>
            <DialogDescription>
              Upload a backup file to restore your data
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* File Upload */}
            {!restorePreview && !restoreResult && (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".zip"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="backup-file-input"
                />
                <FileUp className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">
                  {restoreLoading ? "Analyzing backup file..." : "Select a backup ZIP file"}
                </p>
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={restoreLoading}
                >
                  {restoreLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Choose Backup File
                </Button>
                <p className="text-xs text-gray-400 mt-4">
                  Supported: pos_backup_*.zip, pos_critical_backup_*.zip, pos_financial_backup_*.zip
                </p>
              </div>
            )}

            {/* Preview */}
            {restorePreview && !restoreResult && (
              <div className="space-y-4">
                <Alert className="bg-blue-50 border-blue-200">
                  <FileJson className="h-4 w-4" />
                  <AlertTitle>Backup File: {restorePreview.filename}</AlertTitle>
                  <AlertDescription>
                    Size: {restorePreview.file_size_mb} MB • 
                    {Object.keys(restorePreview.collections || {}).length} collections found
                  </AlertDescription>
                </Alert>

                {/* Restore Mode */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Restore Mode</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RadioGroup value={restoreMode} onValueChange={setRestoreMode}>
                      <div className="flex items-start space-x-3 p-3 rounded border bg-green-50 border-green-200">
                        <RadioGroupItem value="merge" id="merge" />
                        <Label htmlFor="merge" className="cursor-pointer flex-1">
                          <span className="font-medium text-green-700">Merge (Safe - Recommended)</span>
                          <p className="text-xs text-green-600 mt-1">
                            Adds new records from backup. Keeps existing data unchanged. Will not delete anything.
                          </p>
                        </Label>
                      </div>
                      <div className="flex items-start space-x-3 p-3 rounded border bg-red-50 border-red-200 mt-2">
                        <RadioGroupItem value="replace" id="replace" />
                        <Label htmlFor="replace" className="cursor-pointer flex-1">
                          <span className="font-medium text-red-700">Replace (Caution!)</span>
                          <p className="text-xs text-red-600 mt-1">
                            ⚠️ DELETES all current data in selected collections and replaces with backup data.
                          </p>
                        </Label>
                      </div>
                    </RadioGroup>
                  </CardContent>
                </Card>

                {/* Collection Selection */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-sm">Select Collections to Restore</CardTitle>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={selectAllCollections}>
                          Select All
                        </Button>
                        <Button variant="ghost" size="sm" onClick={deselectAllCollections}>
                          Deselect All
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-60 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10"></TableHead>
                            <TableHead>Collection</TableHead>
                            <TableHead className="text-right">In Backup</TableHead>
                            <TableHead className="text-right">Currently</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(restorePreview.collections || {})
                            .filter(([_, info]) => info.can_restore)
                            .sort((a, b) => (b[1]?.backup_count || 0) - (a[1]?.backup_count || 0))
                            .map(([name, info]) => (
                              <TableRow key={name}>
                                <TableCell>
                                  <Checkbox
                                    checked={selectedCollections.includes(name)}
                                    onCheckedChange={() => toggleCollection(name)}
                                    disabled={info.backup_count === 0}
                                  />
                                </TableCell>
                                <TableCell>
                                  <p className="font-medium text-sm">{info.display_name}</p>
                                  <p className="text-xs text-gray-400">{name}</p>
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm">
                                  {info.backup_count?.toLocaleString() || 0}
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm text-gray-500">
                                  {info.current_count?.toLocaleString() || 0}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {selectedCollections.length} collection(s) selected
                    </p>
                  </CardContent>
                </Card>

                {restoreMode === "replace" && (
                  <Alert className="bg-red-50 border-red-500">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertTitle className="text-red-700">Warning: Replace Mode</AlertTitle>
                    <AlertDescription className="text-red-600">
                      This will permanently DELETE all current data in the selected collections 
                      before importing the backup. This cannot be undone! 
                      <strong> Create a backup first if you have not already.</strong>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Restore Result */}
            {restoreResult && (
              <div className="space-y-4">
                <Alert className="bg-green-50 border-green-500">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-700">Restore Complete!</AlertTitle>
                  <AlertDescription className="text-green-600">
                    Successfully restored {restoreResult.total_restored?.toLocaleString()} records
                    {restoreResult.total_skipped > 0 && ` (${restoreResult.total_skipped} skipped - already exist)`}
                  </AlertDescription>
                </Alert>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Restore Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Collection</TableHead>
                          <TableHead className="text-right">Restored</TableHead>
                          <TableHead className="text-right">Skipped</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(restoreResult.collections || {}).map(([name, info]) => (
                          <TableRow key={name}>
                            <TableCell className="font-medium">{name}</TableCell>
                            <TableCell className="text-right font-mono">
                              {info.restored || 0}
                            </TableCell>
                            <TableCell className="text-right font-mono text-gray-500">
                              {info.skipped || 0}
                            </TableCell>
                            <TableCell>
                              <Badge variant={info.status === "error" ? "destructive" : "outline"} 
                                     className={info.status !== "error" ? "bg-green-50 text-green-700" : ""}>
                                {info.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {restoreResult.errors?.length > 0 && (
                  <Alert className="bg-yellow-50 border-yellow-500">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Some Errors Occurred</AlertTitle>
                    <AlertDescription>
                      <ul className="list-disc list-inside text-sm mt-2">
                        {restoreResult.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            {!restoreResult ? (
              <>
                <Button variant="outline" onClick={closeRestoreDialog}>
                  Cancel
                </Button>
                {restorePreview && (
                  <Button 
                    onClick={handleRestore}
                    disabled={restoreLoading || selectedCollections.length === 0}
                    className={restoreMode === "replace" ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}
                  >
                    {restoreLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4 mr-2" />
                    )}
                    Restore {selectedCollections.length} Collection(s)
                  </Button>
                )}
              </>
            ) : (
              <Button onClick={closeRestoreDialog}>
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BackupPage;
